import { IntlShape } from 'react-intl'
import { isToday, isYesterday } from 'date-fns'
import { utcToZonedTime } from 'date-fns-tz'

import { TIMEZONE } from 'common/helpers/commonConstants'
import { formatStringDateToLocal } from 'common/helpers/formatStringDateToLocal'
import {
  MessagePerson,
  MessageTargets,
  Notification,
  NotificationType,
  SchedulerExtraParams,
  InterfaceExtraParams
} from 'common/types/notifications'

export const debounce = (func: Function, delay: number) => {
  let timeoutId: NodeJS.Timeout
  return (...args: any[]) => {
    clearTimeout(timeoutId)
    timeoutId = setTimeout(() => {
      func.apply(null, args)
    }, delay)
  }
}
export const handleScroll = (
  event: React.UIEvent<HTMLUListElement>,
  count: number,
  page: number,
  totalPages: number,
  notificationsLength: number,
  setPage: (value: number) => void,
  filterChange?: boolean,
  setFilterChange?: (value: boolean) => void
): void => {
  const target = event.target as HTMLUListElement
  if (count !== notificationsLength && page !== totalPages) {
    if (target.scrollTop + target.clientHeight >= target.scrollHeight - 20) {
      setPage(page + 1)
      if (filterChange) {
        setFilterChange?.(false)
      }
    }
  }
}
const translationsRegexPatterns = {
  'app.pages.notifications.scheduler.taskFinished': /The task (.*?) has finished with state (.*?)$/,
  'app.pages.notifications.workflow.taskReady': /The task (.*?) of the process (.*?) is ready to be started/,
  'app.pages.notifications.workflow.taskOverdueExtended': /The task (.*?) of the process (.*?) is overdue. This may affect the completion of your task (.*?)$/,
  'app.pages.notifications.workflow.taskOverdue': /The task (.*?) of the process (.*?) is overdue/,
  'app.pages.notifications.workflow.taskChangedStatus': /The task (.*?) of the process (.*?) has changed status from (.*?) to (.*?)$/,
  'app.pages.notifications.workflow.processStarted': /The process (.*?) has started/,
  'app.pages.notifications.workflow.processUpToDateAgain': /The process (.*?) is up to date again/,
  'app.pages.notifications.workflow.wroteComment': /(.*?) wrote the following comment in task (.*?) of the process (.*?): (.*?)$/
}
export const translateMessage = (intl: IntlShape, message: string) => {
  for (const [key, pattern] of Object.entries(translationsRegexPatterns)) {
    const matches = message.match(pattern)
    if (matches) {
      const matchesObject: { [key: string]: string } = {}
      matches.forEach((match, index) => (matchesObject[`match${index}`] = match))
      let translatedMessage = intl.formatMessage({ id: key }, matchesObject).toString() || ''
      if (key === 'app.pages.notifications.scheduler.taskFinished') {
        translatedMessage = translatedMessage
          .replace(
            /SUCCESS/g,
            intl.formatMessage({ id: 'app.pages.app.taskManager.success' }).toUpperCase()
          )
          .replace(
            /FAILURE/g,
            intl.formatMessage({ id: 'app.pages.app.taskManager.failure' }).toUpperCase()
          )
      } else {
        translatedMessage = translatedMessage
          .replace(
            /Not ready to start/g,
            intl.formatMessage({ id: 'app.pages.app.processManager.taskStatus.not_ready_to_start' })
          )
          .replace(
            /Not started/g,
            intl.formatMessage({ id: 'app.pages.app.processManager.taskStatus.not_started' })
          )
          .replace(
            /In progress/g,
            intl.formatMessage({ id: 'app.pages.app.processManager.taskStatus.in_progress' })
          )
          .replace(
            /Pending review/g,
            intl.formatMessage({ id: 'app.pages.app.processManager.taskStatus.pending_review' })
          )
          .replace(
            /Completed/g,
            intl.formatMessage({ id: 'app.pages.app.processManager.taskStatus.completed' })
          )
      }
      return translatedMessage
    }
  }
  // Return original message
  return message
}
export const NOTIFICATIONS_PAGE_SIZE = 25
export const GENERAL_NOTIFICATIONS_PAGE_SIZE = 5
export const NOTIFICATIONS_QUERY_KEY = 'notifications'
export const UNREAD_NOTIFICATIONS_FILTER = 'unread_date'

/** The app's title, or the folder it lives in when whoever created the notification did not know the title. */
export const getNotificationAppName = (notification: Notification): string => {
  if (notification.app_name) {
    return notification.app_name
  }
  const segments = (notification.app_path ?? '').split('/').filter(Boolean)
  const last = segments[segments.length - 1] ?? ''
  return last.endsWith('.ppl') ? segments[segments.length - 2] ?? '' : last
}

const formatDay = (intl: IntlShape, value: string) =>
  intl.formatDate(new Date(value), { day: 'numeric', month: 'short', timeZone: TIMEZONE })

/** "10:12" today, "Yesterday" or "28 Oct" before, in the browser's time zone. */
export const formatNotificationTime = (intl: IntlShape, value: string): string => {
  const date = utcToZonedTime(new Date(value), TIMEZONE)
  if (isToday(date)) {
    return formatStringDateToLocal(value, 'HH:mm')
  }
  if (isYesterday(date)) {
    return intl.formatMessage({ id: 'app.pages.notifications.yesterday' })
  }
  return formatDay(intl, value)
}

/** "Today 10:12", "Yesterday 10:12" or "28 Oct 10:12", in the browser's time zone. */
export const formatNotificationDateTime = (intl: IntlShape, value: string): string => {
  const date = utcToZonedTime(new Date(value), TIMEZONE)
  const day = isToday(date)
    ? intl.formatMessage({ id: 'app.pages.notifications.today' })
    : isYesterday(date)
    ? intl.formatMessage({ id: 'app.pages.notifications.yesterday' })
    : formatDay(intl, value)
  return `${day} ${formatStringDateToLocal(value, 'HH:mm')}`
}

/** "@Supply, @S&OP team and Laura Gómez": the groups as @mentions, then the people. */
export const formatMessageTargets = (intl: IntlShape, to: MessageTargets): string =>
  intl.formatList(
    [
      ...to.departments.map((name) => `@${name}`),
      ...to.teams.map((name) => `@${name}`),
      ...to.users
    ],
    { type: 'conjunction' }
  )

export type MessageTarget =
  | { kind: 'received'; messageId: number; replyNotification?: Notification }
  | { kind: 'sent'; messageId: number; partnerId: number | null }

/**
 * What a message notification opens. A reply opens the thread of the message it
 * answers: as its sender, on that person's conversation; as a recipient, on the
 * message itself, keeping the reply's notification for the detail's actions.
 */
export const resolveMessageTarget = (notification: Notification): MessageTarget | null => {
  const message = notification.user_message
  if (!message) {
    return null
  }
  if (message.parent === null) {
    return { kind: 'received', messageId: message.id }
  }
  return notification.usercompany === message.parent_sender_id
    ? { kind: 'sent', messageId: message.parent, partnerId: message.sender?.id ?? null }
    : { kind: 'received', messageId: message.parent, replyNotification: notification }
}

export const messagePersonName = (intl: IntlShape, person: MessagePerson | null): string =>
  person?.name ?? intl.formatMessage({ id: 'app.pages.notifications.removedUser' })

export const isOverdueNotification = (notification: Notification): boolean =>
  notification.notification_type === NotificationType.Workflow &&
  /is overdue/.test(notification.message)

export const getNotificationLink = (
  notificationType: NotificationType,
  extraParams?: object | SchedulerExtraParams | InterfaceExtraParams
): string | undefined => {
  switch (notificationType) {
    case NotificationType.Workflow:
      return '/my-tasks'
    case NotificationType.Scheduler: {
      const taskId = (extraParams as SchedulerExtraParams | undefined)?.task_id
      return taskId !== undefined ? `/tasks/${taskId}/show-task-logs` : '/tasks'
    }
    case NotificationType.Interface: {
      const interfaceId = (extraParams as InterfaceExtraParams | undefined)?.interface_id
      return interfaceId !== undefined ? `/interfaces/${interfaceId}` : undefined
    }
    case NotificationType.Code:
      return '/code'
    case NotificationType.FileManager:
      return '/files'
    case NotificationType.VersionManager:
      return '/versions'
    case NotificationType.ScenarioManager:
      return '/scenarios/scenario-manager'
    case NotificationType.AppConsolidation:
      return '/app-consolidation'
    case NotificationType.AutomationTests:
      return '/automation-tests'
    default:
      return undefined
  }
}
